import { UserIdentity } from "../types/user";

export interface CachedUserProfile {
  id: string;
  email?: string;
  name?: string;
  profile_image_url?: string;
  university?: string;
  university_email?: string;
  bio?: string;
  age?: number;
  major?: string;
  education_level?: "high_school" | "bachelors" | "masters" | "phd" | "other";
  status?: "applying" | "in_university" | "graduated";
  subscription_plan?: string;
  roles?: string[];
  onboarded?: boolean;
  // Identity data
  identity?: UserIdentity;
  // Timestamps for cache validation
  profileCachedAt?: number;
  identityCachedAt?: number;
}

export interface CacheMetrics {
  essayCount?: number;
  analysisCount?: number;
  lastFetchedAt?: number;
}

/**
 * Service to manage cached user profile data across the application
 */
export class ProfileCacheService {
  private static readonly CACHE_KEY_PREFIX = "user_profile_";
  private static readonly METRICS_KEY_PREFIX = "user_metrics_";
  private static readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  private static readonly IDENTITY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get cached user profile data
   */
  static getCachedProfile(userId: string): CachedUserProfile | null {
    try {
      const cacheKey = this.CACHE_KEY_PREFIX + userId;
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const profile: CachedUserProfile = JSON.parse(cached);

      // Check if profile cache is still valid
      const now = Date.now();
      const profileExpired =
        profile.profileCachedAt &&
        now - profile.profileCachedAt > this.CACHE_DURATION;

      if (profileExpired) {
        // Profile data is expired but we can still return it for immediate use
        // The calling code should refresh it in the background
        return { ...profile, profileCachedAt: undefined }; // Mark as stale
      }

      return profile;
    } catch (error) {
      console.error("Error getting cached profile:", error);
      return null;
    }
  }

  /**
   * Cache user profile data
   */
  static setCachedProfile(
    userId: string,
    profile: Partial<CachedUserProfile>
  ): void {
    try {
      const cacheKey = this.CACHE_KEY_PREFIX + userId;
      const existing = this.getCachedProfile(userId) || { id: userId };

      const updated: CachedUserProfile = {
        ...existing,
        ...profile,
        profileCachedAt: Date.now(),
      };

      localStorage.setItem(cacheKey, JSON.stringify(updated));
    } catch (error) {
      console.error("Error caching profile:", error);
    }
  }

  /**
   * Cache user identity data specifically
   */
  static setCachedIdentity(userId: string, identity: UserIdentity): void {
    try {
      const existing = this.getCachedProfile(userId) || { id: userId };
      const updated: CachedUserProfile = {
        ...existing,
        identity,
        identityCachedAt: Date.now(),
      };

      const cacheKey = this.CACHE_KEY_PREFIX + userId;
      localStorage.setItem(cacheKey, JSON.stringify(updated));
    } catch (error) {
      console.error("Error caching identity:", error);
    }
  }

  /**
   * Get cached identity data if still valid
   */
  static getCachedIdentity(userId: string): UserIdentity | null {
    try {
      const profile = this.getCachedProfile(userId);
      if (!profile?.identity || !profile.identityCachedAt) return null;

      const now = Date.now();
      const identityExpired =
        now - profile.identityCachedAt > this.IDENTITY_CACHE_DURATION;

      if (identityExpired) {
        return null; // Identity cache expired
      }

      return profile.identity;
    } catch (error) {
      console.error("Error getting cached identity:", error);
      return null;
    }
  }

  /**
   * Cache user metrics (essay count, analysis count, etc.)
   */
  static setCachedMetrics(userId: string, metrics: CacheMetrics): void {
    try {
      const cacheKey = this.METRICS_KEY_PREFIX + userId;
      const metricsWithTimestamp = {
        ...metrics,
        lastFetchedAt: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(metricsWithTimestamp));
    } catch (error) {
      console.error("Error caching metrics:", error);
    }
  }

  /**
   * Get cached metrics if still valid
   */
  static getCachedMetrics(userId: string): CacheMetrics | null {
    try {
      const cacheKey = this.METRICS_KEY_PREFIX + userId;
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const metrics: CacheMetrics = JSON.parse(cached);

      // Check if metrics cache is still valid (shorter duration for metrics)
      const now = Date.now();
      const metricsExpired =
        metrics.lastFetchedAt && now - metrics.lastFetchedAt > 2 * 60 * 1000; // 2 minutes for metrics

      if (metricsExpired) {
        return null;
      }

      return metrics;
    } catch (error) {
      console.error("Error getting cached metrics:", error);
      return null;
    }
  }

  /**
   * Check if profile data is stale and needs refresh
   */
  static isProfileStale(userId: string): boolean {
    const profile = this.getCachedProfile(userId);
    if (!profile?.profileCachedAt) return true;

    const now = Date.now();
    return now - profile.profileCachedAt > this.CACHE_DURATION;
  }

  /**
   * Check if identity data is stale and needs refresh
   */
  static isIdentityStale(userId: string): boolean {
    const profile = this.getCachedProfile(userId);
    if (!profile?.identityCachedAt) return true;

    const now = Date.now();
    return now - profile.identityCachedAt > this.IDENTITY_CACHE_DURATION;
  }

  /**
   * Clear all cached data for a user
   */
  static clearUserCache(userId: string): void {
    try {
      const profileKey = this.CACHE_KEY_PREFIX + userId;
      const metricsKey = this.METRICS_KEY_PREFIX + userId;
      localStorage.removeItem(profileKey);
      localStorage.removeItem(metricsKey);
    } catch (error) {
      console.error("Error clearing user cache:", error);
    }
  }

  /**
   * Clear all cached data for all users (useful for debugging or major updates)
   */
  static clearAllCache(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (
          key.startsWith(this.CACHE_KEY_PREFIX) ||
          key.startsWith(this.METRICS_KEY_PREFIX)
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Error clearing all cache:", error);
    }
  }

  /**
   * Get cache statistics for debugging
   */
  static getCacheStats(): { profileCaches: number; metricsCaches: number } {
    try {
      const keys = Object.keys(localStorage);
      const profileCaches = keys.filter((key) =>
        key.startsWith(this.CACHE_KEY_PREFIX)
      ).length;
      const metricsCaches = keys.filter((key) =>
        key.startsWith(this.METRICS_KEY_PREFIX)
      ).length;
      return { profileCaches, metricsCaches };
    } catch (error) {
      console.error("Error getting cache stats:", error);
      return { profileCaches: 0, metricsCaches: 0 };
    }
  }
}
